import {
  Badge,
  Box,
  HStack,
  Icon,
  Image,
  Text,
  VStack,
} from '@chakra-ui/react';
import { capitalizeWord } from '@web-app/utils/format.utils';
import { MdRestaurant } from 'react-icons/md';

import { JournalEntryCard } from '../JournalEntryCard/JournalEntryCard';
import { FoodJournalEntry } from '../types';

export interface FoodEntryCardProps {
  entry: FoodJournalEntry;
}
const nutritionFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 1,
});
/**
 * Displays a food journal entry with meal type, description, image, and comments.
 */
export const FoodEntryCard: React.FC<FoodEntryCardProps> = ({ entry }) => {
  const hasImage = entry.imageUrl && !entry.imageUrl.includes('missing.png');
  const thumbnail = hasImage ? entry.imageUrl : undefined;

  return (
    <JournalEntryCard
      entryId={entry.id}
      icon={<Icon as={MdRestaurant} color="text.muted" boxSize={5} />}
      title={
        <Text fontSize="sm" fontWeight={600} color="text.default">
          {entry.mealType
            ? capitalizeWord(entry.mealType?.toLowerCase())
            : 'Meal'}
        </Text>
      }
      timezone={entry.timezone}
      time={entry.createdAt}
      comments={entry.comments}
      thumbnail={thumbnail ?? undefined}
    >
      {({ isExpanded: expanded }) => (
        <VStack align="stretch" spacing={2}>
          {/* Key Info (always visible) */}
          {entry.description && (
            <Text
              fontSize="sm"
              color="text.default"
              noOfLines={expanded ? undefined : 2}
            >
              {entry.description}
            </Text>
          )}

          {/* Expanded Content */}
          {expanded && (
            <>
              {/* Image */}
              {hasImage && entry.imageUrl && (
                <Box borderRadius="md" overflow="hidden" mt={2}>
                  <Image
                    src={entry.imageUrl}
                    alt="Food entry"
                    width="100%"
                    maxHeight="400px"
                    objectFit="contain"
                  />
                </Box>
              )}

              {/* Emotions */}
              {entry.emotions.length > 0 && (
                <Box>
                  <Text
                    fontSize="xs"
                    fontWeight={600}
                    color="text.muted"
                    mb={1}
                  >
                    How you felt:
                  </Text>
                  <HStack spacing={1} flexWrap="wrap">
                    {entry.emotions.map((emotion, idx) => (
                      <Badge
                        key={idx}
                        variant="subtle"
                        colorScheme="blue"
                        fontSize="xs"
                      >
                        {emotion}
                      </Badge>
                    ))}
                  </HStack>
                </Box>
              )}

              {/* Nutritional Metrics */}
              {entry.nutritionalMetrics.length > 0 && (
                <Box>
                  <Text
                    fontSize="xs"
                    fontWeight={600}
                    color="text.muted"
                    mb={1}
                  >
                    Nutrition:
                  </Text>
                  <VStack align="stretch" spacing={1}>
                    {entry.nutritionalMetrics.map((metric) => (
                      <HStack key={metric.name} justify="space-between">
                        <Text fontSize="xs" color="text.default">
                          {metric.name}
                        </Text>
                        <Text
                          fontSize="xs"
                          color="text.default"
                          fontWeight={600}
                        >
                          {nutritionFormatter.format(metric.value)}{' '}
                          {metric.unit ?? ''}
                        </Text>
                      </HStack>
                    ))}
                  </VStack>
                </Box>
              )}
            </>
          )}
        </VStack>
      )}
    </JournalEntryCard>
  );
};

FoodEntryCard.displayName = 'FoodEntryCard';
